import React, { useState, useEffect } from "react";
import {useLoaderData} from 'react-router-dom';
import Group2 from "../components/Group2";
import { getGroupContent, updateBlockOrderInDB, initialisePageSkins } from "../Users";
import { useUser } from "../components/UserContext";
import '../texteditstyle.css';

export async function loader({params}) {
  const groupLink = params.groupLink;
  const groupContent = await getGroupContent(groupLink);
  if (!groupContent) {
      throw new Response("", {
          status: 404,
          statusText: "Not Found",
        });
  }
  return {groupContent};
}

export default function GroupViewer() {
  const {groupContent} = useLoaderData();
  const {currentUser} = useUser();
  const [groups, setGroups] = useState([]);
  const [displayedGroups, setDisplayedGroups] = useState([]);
  const [heights, setHeights] = useState([]);
  const groupDisplayOrder = true;
  
  const recalcTimerRef = React.useRef(null);
  const recalcArgsRef  = React.useRef(null);
  const groupsRef = React.useRef(groups);
  React.useEffect(() => { groupsRef.current = groups; }, [groups]);

  useEffect(() => { //initialise
    initialisePageSkins(groupContent.groups[0].skin);
    document.title = `Group Viewer`;


    const initializegroupStartingHeights = () => {
      
      let initializedHeights = [];
      for (let i = 0; i < groupContent.groups.length; i++) {
        initializedHeights.push(40)
      }
      setHeights(initializedHeights);
    };

    if (groupContent.groups.length >= 0) {
    setGroups(() => {
      //for each group,, go through all their blocks, change column start and column end to 0 and 12
      let groups = [...groupContent.groups];
      if (window.innerWidth < 600) {
        groups.forEach((group) => {
          for (let i = 0; i < group.blocks.length; i++) {
            group.blocks[i].columnStart = 0;
            group.blocks[i].columnEnd = 12;
          }
        })
      }
      return (groups);
    })
      if (groupContent.groups.length > 0) {
        initializegroupStartingHeights();
      }
    } 
  }, [groupContent])

  useEffect(() => { //reverse order
    if (!groupDisplayOrder) {
      setDisplayedGroups(groups.toReversed());
    } else {
      setDisplayedGroups(groups);
    }
  }, [groupDisplayOrder, groups])

  function updateGroupHeight(order, newHeight) {
    setHeights(prevHeights => {
      const updatedHeights = [...prevHeights];
      updatedHeights[order] = newHeight;
      return updatedHeights;
    });
  }  
  
  function cleanOrder(things) {
    return things.map((blockItem, index) => ({
      ...blockItem, 
      order: index 
    }));
  }  
  
  function recalcBlockOrder(groupId, groupOrder, originalSH) {
    if (recalcTimerRef.current) clearTimeout(recalcTimerRef.current);

    recalcArgsRef.current = [groupOrder, originalSH];

    recalcTimerRef.current = setTimeout(() => {
      const [gOrder, oSH] = recalcArgsRef.current;

      const SHOnly = [];
      const CSOnly = [];
      const SHMostlyInOrder = [];
      const placementsPrettyMuch = [];
      const finalPlacements = []; 

      for (let i = 0; i < oSH.length; i++) {
        SHOnly.push(oSH[i].SH);
        CSOnly.push(oSH[i].CS);
      }
      const SHDec = [...SHOnly];
      const processedIndexes = new Array(SHOnly.length).fill(false); 
      for (let i = 0; i < oSH.length; i++) {
        const newLSH = Math.min(...SHDec);  
        const iOfNLSH = SHOnly.findIndex((sh, idx) => sh === newLSH && !processedIndexes[idx]); 
        SHMostlyInOrder.push(newLSH);  
        placementsPrettyMuch.push(iOfNLSH); 
        const fakeIndex = SHDec.indexOf(newLSH); 
        SHDec.splice(fakeIndex, 1); 
        processedIndexes[iOfNLSH] = true;  
      }
      for (let i = 0; i < oSH.length; i++) {
        const dupeIndexes = [];
        SHMostlyInOrder.forEach((SH, index) => {
          if (SH === SHMostlyInOrder[i]) dupeIndexes.push(index);
        });
        if (dupeIndexes.length > 1) {
          const dupePlacementIndexes = [];
          dupeIndexes.forEach((index) => {
            dupePlacementIndexes.push(index); 
          });
          const sortedDupeIndexes = dupePlacementIndexes.sort((a, b) => {
            const originalIndexA = placementsPrettyMuch[a];  
            const originalIndexB = placementsPrettyMuch[b]; 
            return CSOnly[originalIndexA] - CSOnly[originalIndexB];  
          });
          sortedDupeIndexes.forEach((sortedIndex) => {
            finalPlacements.push(placementsPrettyMuch[sortedIndex]);
          });
          i += dupeIndexes.length - 1;
        } else {
          finalPlacements.push(placementsPrettyMuch[i]);
        }
      }

      const needsReorder = finalPlacements.some((place, index) => place !== index);

      if (needsReorder) {
        const updatedSH = [...oSH];
        const reorderedSH = [];
        finalPlacements.forEach((placement, i) => {
          reorderedSH[i] = updatedSH[placement];
        });

        finalPlacements.pop();

        const currentGroups = groupsRef.current;
        const group = currentGroups?.[gOrder];
        if (!group) return;

        updateBlockOrderInDB(group.id, finalPlacements, group.blocks?.length ?? 0);

        setTimeout(() => {
          setGroups(prevGroups => {
            const updatedGroups = [...prevGroups];
            const groupToUpdate = updatedGroups[gOrder];
            if (!groupToUpdate) return prevGroups;

            const movingBlocks = [...groupToUpdate.blocks];
            let reorderedBlocks = [];
            finalPlacements.forEach((placement, i) => {
              reorderedBlocks[i] = movingBlocks[placement];
            });
            reorderedBlocks = cleanOrder(reorderedBlocks);

            updatedGroups[gOrder] = { ...groupToUpdate, blocks: reorderedBlocks };
            return updatedGroups;
          });
        }, 0);
      }
    }, 30);

    return originalSH;
  }

  return (
    <div>
      {groupContent.groups[0].skin.backgroundImage && 
        <img id="page-background" src={groupContent.groups[0].skin.backgroundImage} alt="background"/>
      }
      <div id="block-area" style={{left:`${window.innerWidth <= 600? 6 : (98 - groupContent.groups[0].skin.groupWidth) / 2}vw`, top:"3vw"}}>
        {displayedGroups.map((groupItem, index) => (
          <Group2
            key={groupItem.id}
            groupId={groupItem.id}
            groupOrder={groupItem.order}
            title={groupItem.title}
            blocks={groupItem.blocks}
            height={heights[groupItem.order]}
            onHeightChange={updateGroupHeight}
            recalcOrder={recalcBlockOrder}
            isProfile={groupItem.isProfile}
            isPrivate={groupItem.isPrivate}
            pageAuthor={groupItem.authorId}
            currentUser={currentUser? currentUser.id : 1}
            pageSkin={groupItem.skin}
            isSearch={true}
            pageCode={groupItem.pageCode}
          />
        ))}
      </div>
      <div id="space"></div>
    </div>
  );
}